import { scaleLinear, scaleQuantize } from 'd3-scale';
import { area, curveStepAfter, line } from 'd3-shape';
import {
  ascending,
  median,
  // merge,
  quantile,
  range,
} from 'd3-array';
import { timeHour, timeMinute } from 'd3-time';

import { defined, hoursOfDay, padFormat, pathTween } from './AGPGraph';
import { exposeToClipboard,  } from '../utils/clipboard';
import { nameKey } from '../utils/dataUtils';
import { getSlowTransition } from '../utils/svgUtils';
import './Dashboard.css';

function groupQuantiles(dailyEntries) {
  const groupedHours = dailyEntries.reduce((o, day) => {
    day.forEach(entry => {
      const hourlyDoses = {};
      const startTime = new Date(entry.time);
      const endTime = new Date(entry.endTime);
      const coveredHours = timeHour.range(startTime, endTime).map(d => d.getHours());
      let hour = startTime.getHours();
      if (coveredHours[0] !== hour) {
        // Add the first hour
        coveredHours.unshift(hour);
      }
      coveredHours.forEach((h, i, a) => {
        if (!hourlyDoses[h]) {
          hourlyDoses[h] = 0;
        }
        const duration = (i === a.length - 1) ? timeMinute.count(timeHour(endTime), endTime) : 60;
        hourlyDoses[h] += duration * entry.rate / 60;
      });
      Object.keys(hourlyDoses).forEach((h) => {
        o[h].push(hourlyDoses[h]);
      });
    });
    return o;
  }, hoursOfDay.reduce((o, h) => {
    o[h] = [];
    return o;
  }, {}));

  const groupedQuantiles = {};
  Object.keys(groupedHours).forEach((h) => {
    const basalValues = groupedHours[h].sort(ascending);
    groupedQuantiles[h] = {
      count: basalValues.length,
      median: median(basalValues),
      q10: quantile(basalValues, 0.10),
      q25: quantile(basalValues, 0.25),
      q75: quantile(basalValues, 0.75),
      q90: quantile(basalValues, 0.90),
    };
  });
  return groupedQuantiles;
};

function pathGenerators({ x, y }) {
  return [
    {
      name: 'q90',
      generator: area()
        .defined(defined)
        .x(x)
        .y0(d => (y(d.value.q10)))
        .y1(d => (y(d.value.q90)))
        .curve(curveStepAfter),
    },
    {
      name: 'interquartile',
      generator: area()
        .defined(defined)
        .x(x)
        .y0(d => (y(d.value.q25)))
        .y1(d => (y(d.value.q75)))
        .curve(curveStepAfter),
    },
    {
      name: 'median line',
      generator: line()
        .defined(defined)
        .x(x)
        .y(d => (y(d.value.median)))
        .curve(curveStepAfter),
    },
  ];
}

function profileGenerator({ x, y }) {
  const generator = line()
    .x(x)
    .y(d => (y(d.value)))
    .curve(curveStepAfter);
  return function profilePath(d) {
    return generator([...d, { time: '24:00', value: d[d.length - 1].value }]);
  };
}

function isTBR(basalEntry) {
  return !basalEntry.id.indexOf('tbr-');
}

function parseMoment(d) {
  const { time } = d;
  const parts = time.split(':');
  const moment = +parts[0] + (+parts[1] / 60);
  return moment;
}

function getProfileTitleGenerator(t) {
  return function (p) {
    return p.profile.basal.map((step) => `${step.time}: ${step.value} ${t('insulin.iu/h')}`).join('\n');
  }
}

function render(selection) {
  const {
    data = {},
    profile,
    t,
  } = selection.datum();

  const dailyEntries = Object.values(data);

  const {
    hours = hoursOfDay,
    width = 720,
    height = 170,
  } = selection.datum();

  let firstHour = hours[0];
  const lastHour = hours[hours.length - 1];

  const hourlyNest = groupQuantiles(dailyEntries);
  const basalData = hours.map(h => ({
    key: padFormat(((firstHour > lastHour) && (h >= firstHour)) ? h - 24 : h),
    value: hourlyNest[h] || {},
  }));
  if (firstHour > lastHour) {
    firstHour += -24;
  }

  // Fill the last part (hours indicate start, not end times)
  // Also, to bend the splines right, copy values before start and after end
  basalData.push(
    { key: padFormat(lastHour + 1), value: hourlyNest[(lastHour + 1) % 24] },
    { key: padFormat(lastHour + 2), value: hourlyNest[padFormat((lastHour + 2) % 24)] },
    { key: padFormat(lastHour + 3), value: hourlyNest[padFormat((lastHour + 3) % 24)] },
  );
  basalData.unshift(
    { key: padFormat(firstHour - 2), value: hourlyNest[padFormat((firstHour + hours.length - 2) % 24)] },
    { key: padFormat(firstHour - 1), value: hourlyNest[padFormat((firstHour + hours.length - 1) % 24)] },
  );

  const hourWidth = width / hours.length;
  const domain = [+basalData[0].key, +basalData[basalData.length - 1].key];

  const hourScale = scaleQuantize()
    .domain(domain)
    .range(range(-2, hours.length + 2).map(h => (h * hourWidth)));


  const profileExtent = [0, 24];
  const timeScale = scaleLinear()
    .domain(profileExtent)
    .range(profileExtent.map(h => (h * hourWidth)));
  
  const maxBasal = 3;

  const y = scaleLinear().domain([0, maxBasal]).range([height, 0]);
  const slowTransition = getSlowTransition();

  // Decide whether we think this is an APS or not
  // More than 12 tbr's per day?
  const apsViewEnabled = false;
  const isAPS = dailyEntries.some(values => (values.filter(isTBR).length > 12));
  if (apsViewEnabled && isAPS) {
    renderAPS({ selection, basalData, hourScale, y, width, height, inTransition: slowTransition });
  } else {
    renderProfile({ selection, profile, dailyEntries, timeScale, y,
      inTransition: slowTransition, t });
  }
}

function renderAPS(props) {
  const {
    selection,
    basalData,
    hourScale,
    y,
    width,
    height,
    inTransition,
  } = props;

  const x = hour => hourScale(+hour.key);

  const graphsG = selection
    .selectAll('g.graphs')
    .data([1])
    .join(
      enter => enter.append('g')
        .attr('class', 'basal graphs'),
      update => update
        .classed('basal', true),
      exit => exit.remove(),
    );

  graphsG.selectAll('path.basal')
    .data(pathGenerators({ x, y }), nameKey)
    .join(
      enter => enter.insert('path')
        .attr('class', d => (`basal ${d.name}`))
        .attr('shape-rendering', 'auto')
        .attr('d', d => d.generator(basalData) || `M${width / 2},${height / 2} ${width / 2},${height / 2}`),
      update => update
        .call(update => {
          update
            .transition(inTransition)
            .attrTween('d', pathTween(basalData, 4, width, height));
        }),
      exit => exit.remove(),
    );
}

function renderProfile(props) {
  const {
    selection,
    // dailyEntries,
    timeScale,
    profile,
    y,
    inTransition,
    t,
  } = props;

  const x = step => timeScale(parseMoment(step));
  
  /*
  const merged = merge(dailyEntries);
  const basals = merged.filter(b => !isTBR(b));
  const tbrs = merged.filter(isTBR);
  */

  const graphsG = selection
    .selectAll('g.graphs')
    .data([1])
    .join('g')
      .attr('class', 'profile graphs');
      
  const profilePathGenerator = profileGenerator({ x, y });

  const profileTitleGenerator = getProfileTitleGenerator(t);

  const profilePath = graphsG.selectAll('path.profile')
  .data([profile], nameKey)
  .join(
    (enter) => {
      const path = enter.insert('path')
        .attr('class', 'profile')
        .attr('shape-rendering', 'auto')
        .attr('d', d => profilePathGenerator(d.profile.basal));
      path.append('title')
        .text(profileTitleGenerator);
      return path;
    },
    (update) => {
      const t = update
        .transition(inTransition)
        .attr('d', d => profilePathGenerator(d.profile.basal));
      t.select('title')
        .text(profileTitleGenerator);
      return t;
    },
    exit => exit.remove(),
  );
}

const BasalGraph = {
  init: f => f,
  render,
  destroy: f => f,
};

export default BasalGraph;
